import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { FileDropZone } from "./FileDropZone";
import { useState } from "react";
import { Input } from "../../ui/input";
import { Progress } from "../../ui/progress";
import { Button } from "../../ui/button";
import { useToast } from "../../ui/use-toast";
import { Upload } from "lucide-react";
import { uploadFile } from "../../../lib/storage-utils";
import { supabase } from "../../../lib/supabase";
import { useNavigate } from "react-router-dom";
import React from "react";
import { processStatusService } from "../../../services/supabase/process-status";

interface FileInfo {
  name: string;
  size: number;
  file: File;
}

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadDialog({ open, onOpenChange }: UploadDialogProps) {
  const [progress, setProgress] = useState(0);
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleFile = (file: File) => {
    setSelectedFile({
      name: file.name,
      size: file.size,
      file: file,
    });
    setProgress(0);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast({
        title: "Error",
        description: "Please select a file.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      setProgress(10);
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error("User not authenticated");

      // Create meeting record first
      const { data: meeting, error: meetingError } = await supabase
        .from('meetings')
        .insert({
          user_id: user.id,
          title: title || selectedFile.name,
          status: 'processing',
          type: 'upload',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (meetingError) throw meetingError;

      // Initialize upload status
      await processStatusService.updateStatus(meeting.id, user.id, 'upload', 'processing');

      setProgress(20);

      // Upload file to Supabase Storage
      const uploadedFile = await uploadFile(selectedFile.file, user.id, 'recordings');
      if (!uploadedFile) throw new Error('Failed to upload file');

      setProgress(40);

      // Mark upload as completed
      await processStatusService.updateStatus(meeting.id, user.id, 'upload', 'completed');

      // Process the recording using AssemblyAI
      const response = await fetch('/api/assemblyanalysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recordingPath: uploadedFile.path,
          meetingId: meeting.id,
          userId: user.id,
          recordingType: 'meeting'
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to process recording');
      }

      setProgress(100);

      toast({
        title: "Upload complete",
        description: (
          <div className="flex flex-col gap-2">
            <span>Your file has been uploaded and is being processed.</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/app/status')}
              className="w-full"
            >
              View Status
            </Button>
          </div>
        ),
      });

      // Reset form and close modal
      setSelectedFile(null);
      setTitle("");
      setProgress(0);
      onOpenChange(false);
      
      // Navigate to the status page
      navigate('/app/status');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "An error occurred during upload.",
        variant: "destructive",
      });
      setProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] bg-background text-foreground">
        <DialogHeader>
          <DialogTitle>Upload file</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="space-y-2">
            <Input
              placeholder="Enter meeting title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full"
            />
            <div className="text-sm text-muted-foreground">
              Upload an audio or video file to generate notes and action items.
            </div>
          </div>

          <FileDropZone
            selectedFile={selectedFile}
            onFileSelect={handleFile}
            onFileRemove={() => setSelectedFile(null)}
          />

          {selectedFile && (
            <div className="space-y-4 animate-fade-in">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Upload Progress:</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              <Button
                variant="default"
                onClick={handleSubmit}
                disabled={isUploading}
                className="w-full"
              >
                <Upload className="mr-2 h-4 w-4" />
                {isUploading ? "Uploading..." : "Upload File"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
